import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksRowCellSelectEvent } from '../../events';
import { BlocksDraggableCellMouseDownEvent } from '../../events/blocks-draggable-cell-mousedown';
import { BlocksTableCellExpandEvent } from '../../events/blocks-table-cell-expand';
import { KEYS } from '../../util/event-keys';
import { BlocksDispatchProps, BlocksElement } from '../BlocksElement';
import '../button/button';
import '../checkbox-field/checkbox-field';
import '../icon/icons/chevron-down';
import '../icon/icons/chevron-right';
import '../icon/icons/drag-reorder';
import '../skeleton/skeleton';
import type { TableCellAlignment, TableCellBorderVariant } from './table-cell.model';
import styles from './table-cell.styles';

/**
 * @slot default - The table cell content
 * @fires bl-row-cell-select - emitted when slider value is changed
 * @fires bl-draggable-cell-mousedown - emitted when drag icon is clicked
 * @csspart base - The component's base wrapper.
 * @csspart skeleton - The component's skeleton element.
 * @csspart expandable-button - The component's expandable-button wrapper.
 * @csspart icon-chevron-down - The component's chevron-down icon.
 * @csspart draggable-button - The component's draggable-button wrapper.
 * @csspart icon-drag-reorder - The component's drag-reorder icon.
 * @csspart checkbox - The component's checkbox element.
 * @csspart title - The component's title wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart view-button - The component's view-button wrapper.
 * @csspart icon-chevron-right - The component's chevron-right icon.
 */
export class BlocksTableCell extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Colspan
   * - The number of columns to span the table cell
   */
  @property({ type: Number })
  colspan: number | undefined;

  /**
   * Width
   * - The width of the cell
   */
  @property({ type: String })
  width: string | undefined;

  /**
   * Align variants
   * <ul>
   * <li>**right** renders a table cell with horizontally right aligned content</li>
   * <li>**center** renders a table cell with horizontally center aligned content</li>
   * <li>**space-between** renders a table cell with space between the content</li>
   * <li>**flex-start** renders a table cell with the content aligned inline and to left of the cell</li>
   * <li>**top-left** renders a table cell with horizontally left and vertically top aligned content</li>
   * <li>**top-right** renders a table cell with horizontally right and vertically top aligned content</li>
   * <li>**bottom-left** renders a table cell with horizontally left and vertically bottom aligned content</li>
   * <li>**bottom-right** renders a table cell with horizontally right and vertically bottom aligned content</li>
   * <li>**top-center** renders a table cell with horizontally center and vertically top aligned content</li>
   * <li>**bottom-center** renders a table cell with horizontally center and vertically bottom aligned content</li>
   * </ul>
   */
  @property({ type: String })
  align: TableCellAlignment;

  /**
   * Border variants
   * <ul>
   * <li>**default** adds a border to the bottom of the cell</li>
   * <li>**right** adds a border to the right of the cell</li>
   * </ul>
   */
  @property({ type: String })
  border: TableCellBorderVariant;

  /**
   * Tooltip
   * - If true, adds a tooltip to the cell if the content goes over two lines
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  tooltip: boolean | undefined;

  /**
   * Bulk action
   * - If true, adds the checkbox for bulk actions
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isBulkAction: boolean | undefined;

  /**
   * Bulk action index
   * - Sets the index for events.
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Number })
  bulkActionIndex: number | undefined;

  /**
   * Bulk action selected
   * - Sets the state selected.
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isBulkActionSelected: boolean | undefined;

  /**
   * Expandable
   * - If true, adds the accordion arrow for expandable rows
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isExpandable: boolean | undefined;

  /**
   * Draggable
   * - If true, adds the icon for draggable
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isDraggable: boolean | undefined;

  /**
   * Expandable nested child
   * - If true, adds the padding to cell to align it for expandable nested rows
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isExpandableNestedChild: boolean | undefined;

  /**
   * Expandable active
   * - If true, adds an active state to rotate the expandable icon
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isExpandableActive: boolean | undefined;

  /**
   * Active shadow
   * - Append an active state for the shadow on the table header cell
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isActiveShadow: boolean | undefined;

  /**
   * Frozen
   * - Makes the cell frozen on scroll
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isFrozen: boolean | undefined;

  /**
   * First
   * - Targets the first cells in a row
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isFirst: boolean | undefined;

  /**
   * Mobile view
   * - If true, adds an arrow to indicate the mobile view popover on small screens only
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isMobileView: boolean | undefined;

  /**
   * Loading State
   * - Enables the skeleton loader on cells
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isLoading = false;

  /**
   * Disabled State
   * - table-cell will be disabled when table-row is disabled.
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Property to add label or help text for accessibility purpose
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Label for the right chevron button in mobile view
   */
  @property({ type: String })
  ariaLabelForMobileViewButton: string | undefined;

  /**
   * Text to announce when drag button gets focus
   */
  @property({ type: String })
  startDragText = 'Drag';

  /**
   *
   * To toggle the active state of drag button on touch event
   */
  @state()
  private isDragButtonActive = false;

  /**
   * Gets cat table body
   */
  private get _catTableBody(): HTMLElement | null {
    return this.closest('cat-table-body');
  }

  /**
   * First updated lifecycle hook
   * 1. When the component is first updated, run these functions
   * 2. Fire to set the cell colspan scss
   * 3. Fire to set the cell width
   */
  firstUpdated() {
    this.setColspan(); /* 2 */
    this.setWidth(); /* 3 */
  }

  /**
   * Set the colspan
   * 1) Add inline grid-column style to host to adjust the colspan of the item
   */
  private setColspan() {
    if (this.colspan && this.colspan > 0) {
      const element = this.shadowRoot?.querySelector<BlocksTableCell>('.cat-c-table-cell');

      if (element) {
        element.style.gridColumn = 'auto / span ' + this.colspan; /* 1 */
      }
    }
  }

  /**
   * Set the width
   * 1. Add inline width style to cell to adjust the width
   */
  private setWidth = () => {
    if (this.width) {
      const element = this.shadowRoot?.querySelector<BlocksTableCell>('.cat-c-table-cell');

      if (element) {
        element.style.minWidth = this.width;
        element.style.width = this.width;
      }
    }
  };

  /**
   *
   * @fire bl-draggable-cell-mousedown event
   */
  private dispatchMouseDownEvent(event: MouseEvent | TouchEvent): void {
    this.dispatch({
      eventName: 'bl-draggable-cell-mousedown',
      detailObj: { event, dragRow: this.parentElement }
    }) as BlocksDraggableCellMouseDownEvent;
  }

  private renderLoading(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassName}">
        <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')}></cat-skeleton>
      </div>
    `;
  }

  /**
   * If isDraggable is true, drag button is attached.
   * When there is MouseEvent or TouchEvent in the drag button, bl-draggable-cell-mousedown event is dispatched.
   */

  private renderDraggable(): TemplateResult<1> {
    return html`
      <cat-button
        part="draggable-button"
        exportparts=${this.getExportParts('button-draggable', 'button')}
        class="cat-c-table-cell__draggable-button"
        @mousedown=${(event: MouseEvent) => {
          this.dispatchMouseDownEvent(event);
        }}
        @touchstart=${(event: TouchEvent) => {
          // preventing default behavior to stop firing of mousedown event
          event.preventDefault();
          this.dispatchMouseDownEvent(event);
          this.isDragButtonActive = true;
        }}
        @touchend=${() => {
          this.isDragButtonActive = false;
        }}
        @keydown=${this.handleKeyDown}
        variant="ghost"
        ?hideText=${true}
        .disabled=${this.disabled}
        @focus=${() => {
          // As during browse mode, the space/enter keydown is not fired. Setting role as application and reverting on blur.
          this._catTableBody?.setAttribute('role', 'application');
        }}
        @blur=${() => {
          const bodyRole = this._catTableBody?.getAttribute('role');
          if (bodyRole === 'application') {
            this._catTableBody?.removeAttribute('role');
          }
        }}
      >
        ${this.startDragText}
        <cat-icon-drag-reorder
          part="icon-drag-reorder"
          exportparts=${this.getExportParts('drag-reorder', 'icon')}
          class="cat-c-table-cell__draggable-icon"
          slot="after"
        ></cat-icon-drag-reorder>
      </cat-button>
    `;
  }

  private handleKeyDown(evt: KeyboardEvent) {
    if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
      evt.preventDefault();
      evt.stopPropagation();
      this.dispatch({ eventName: 'bl-keyboard-drag-start', detailObj: { event: evt, dragRow: this.parentElement } });
    }
  }

  private handleExpandableClick() {
    this.dispatch({
      eventName: 'bl-table-cell-expand',
      detailObj: {}
    }) as BlocksTableCellExpandEvent;
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table-cell', {
      'cat-c-table-cell--align-right': this.align === 'right',
      'cat-c-table-cell--align-center': this.align === 'center',
      'cat-c-table-cell--space-between': this.align === 'space-between',
      'cat-c-table-cell--flex-start': this.align === 'flex-start',
      'cat-c-table-cell--align-top-left': this.align === 'top-left',
      'cat-c-table-cell--align-top-right': this.align === 'top-right',
      'cat-c-table-cell--align-bottom-left': this.align === 'bottom-left',
      'cat-c-table-cell--align-bottom-right': this.align === 'bottom-right',
      'cat-c-table-cell--align-top-center': this.align === 'top-center',
      'cat-c-table-cell--align-bottom-center': this.align === 'bottom-center',
      'cat-c-table-cell--border-right': this.border === 'right',
      'cat-c-table-cell--colspan': this.colspan && this.colspan > 0,
      'cat-c-table-cell--frozen': this.isFrozen === true,
      'cat-c-table-cell--tooltip': this.tooltip === true,
      'cat-is-bulk-action': this.isBulkAction === true,
      'cat-is-expandable': this.isExpandable === true,
      'cat-is-expandable-active': this.isExpandableActive === true,
      'cat-is-expandable-nested-child': this.isExpandableNestedChild === true,
      'cat-is-draggable': this.isDraggable === true,
      'cat-is-active-shadow': this.isActiveShadow === true,
      'cat-is-mobile-view': this.isMobileView === true,
      'cat-is-first': this.isFirst === true,
      'cat-c-table-cell--loader': this.isLoading === true,
      'cat-drag-button-active': this.isDragButtonActive === true
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassName);
    }

    return html`
      <div part="base" role="cell" class="${componentClassName}" aria-disabled=${ifDefined(this.disabled)}>
        ${this.isDraggable === true ? this.renderDraggable() : html`${nothing}`}
        ${this.isExpandable === true
          ? html`
              <cat-button
                part="expandable-button"
                exportparts=${this.getExportParts('button-expandable', 'button')}
                class="cat-c-table-cell__expandable-button"
                variant="ghost"
                ?hideText=${true}
                ?disabled=${this.disabled}
                @click=${this.handleExpandableClick}
                label=${ifDefined(this.arialabel)}
                ?expanded=${this.isExpandableActive ? true : false}
              >
                <cat-icon-chevron-down
                  part="icon-chevron-down"
                  exportparts=${this.getExportParts('chevron-down', 'icon')}
                  class="cat-c-table-cell__expandable-icon"
                  slot="after"
                ></cat-icon-chevron-down>
              </cat-button>
            `
          : html`${nothing}`}
        ${this.isBulkAction === true
          ? html`
              <cat-checkbox-field
                part="checkbox"
                exportparts=${this.getExportParts('checkbox-field')}
                class="cat-c-table-cell__checkbox"
                name="bulk-action-cell-checkbox"
                ?hideLabel=${true}
                arialabel=${ifDefined(this.arialabel)}
                .checked=${this.isBulkActionSelected}
                @bl-checkbox-change=${(e: Event) => {
                  e.stopPropagation();
                  const path: Array<unknown> = e.composedPath();
                  const isSelected: boolean = (path[0] as HTMLInputElement)?.checked;
                  this.dispatch({
                    e,
                    eventName: 'bl-row-cell-select',
                    detailObj: { index: this.bulkActionIndex, isSelected }
                  } as BlocksDispatchProps) as BlocksRowCellSelectEvent;
                }}
                ?data-bulk-action-index=${this.bulkActionIndex}
                .disabled=${this.disabled}
              >
              </cat-checkbox-field>
            `
          : html`${nothing}`}
        <div part="title" class="cat-c-table-cell__title">${html` <slot part="part-default"></slot> `}</div>
        ${this.isMobileView === true
          ? html`
              <cat-button
                part="view-button"
                exportparts=${this.getExportParts('button-view', 'button')}
                class="cat-c-table-cell__mobile-view-button"
                variant="ghost"
                label=${ifDefined(this.ariaLabelForMobileViewButton)}
                ?hideText=${true}
              >
                <cat-icon-chevron-right
                  part="icon-chevron-right"
                  exportparts=${this.getExportParts('chevron-right', 'icon')}
                  class="cat-c-table-cell__mobile-view-icon"
                  slot="after"
                ></cat-icon-chevron-right>
              </cat-button>
            `
          : html`${nothing}`}
      </div>
    `;
  }
}

if (customElements.get('cat-table-cell') === undefined) {
  customElements.define('cat-table-cell', BlocksTableCell);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table-cell': BlocksTableCell;
  }
}
